
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiAxios } from "../../../utils/apiAxios";

import type {
  ManagerScheduledOrder,
  ScheduledOrdersApiResponse,
} from "../interface/ScheduledOrder ";

import {
  formatFilterDate,
  mapScheduledOrder,
} from "../utils/scheduledOrders";

export const useScheduledOrders = () => {
  const [orders, setOrders] = useState<ManagerScheduledOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const fetchScheduledOrders = useCallback(async () => {
    setLoading(true);

    try {
      const selectedClinic = JSON.parse(
        sessionStorage.getItem("selectedClinic") || "{}"
      );

      const clinicID = Number(selectedClinic?.clinicID);

      if (!Number.isInteger(clinicID) || clinicID <= 0) {
        setOrders([]);
        toast.error("Clinic ID not found. Please log in again.");
        return;
      }

      const token = sessionStorage.getItem("token") || "";

      const response = await apiAxios.get<ScheduledOrdersApiResponse>(
        "/Dentist/scheduled-orders",
        {
          params: {
            clinicID,
            search: search.trim() || undefined,
            fromDate: formatFilterDate(startDate),
            toDate: formatFilterDate(endDate),
          },
          headers: {
            AuthorizationData: token,
          },
        }
      );

      const result = response.data;

      if (!result?.isSuccess) {
        setOrders([]);
        toast.error(
          result?.status || "Failed to load scheduled orders"
        );
        return;
      }

      const rows = result.data ?? result.dataOne ?? [];
      setOrders(rows.map(mapScheduledOrder));
    } catch (error: unknown) {
      console.error("Error fetching scheduled orders:", error);
      setOrders([]);

      const message =
        (error as { response?: { data?: { status?: string } } })
          ?.response?.data?.status ||
        "Failed to load scheduled orders";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [search, startDate, endDate]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchScheduledOrders();
    }, 400);

    return () => window.clearTimeout(timer);
  }, [fetchScheduledOrders]);

  const clearFilters = useCallback(() => {
    setSearch("");
    setStartDate(null);
    setEndDate(null);
  }, []);

  const uploadScheduledScans = useCallback(
    async (caseID: number, files: File[]): Promise<boolean> => {
      if (files.length === 0) {
        toast.error("Please select scan files first.");
        return false;
      }

      try {
        const formData = new FormData();
        formData.append("CaseID", String(caseID));

        files.forEach((file) => {
          formData.append("Files", file);
        });

        const token = sessionStorage.getItem("token") || "";

        const response = await apiAxios.post(
          "/Dentist/UploadScheduledCaseFiles",
          formData,
          {
            headers: {
              AuthorizationData: token,
            },
          }
        );

        if (!response.data?.isSuccess) {
          toast.error(
            response.data?.status || "Failed to upload scheduled scans."
          );
          return false;
        }

        toast.success("Scheduled scan uploaded successfully.");
        await fetchScheduledOrders();
        return true;
      } catch (error: unknown) {
        console.error("Scheduled scan upload error:", error);

        const message =
          (error as { response?: { data?: { status?: string } } })
            ?.response?.data?.status ||
          "Failed to upload scheduled scans.";

        toast.error(message);
        return false;
      }
    },
    [fetchScheduledOrders]
  );

  return {
    orders,
    loading,
    search,
    setSearch,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    clearFilters,
    fetchScheduledOrders,
    uploadScheduledScans,
  };
};
